import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getOnboarding, updateOnboardingStep } from '../services/api';
import type { OnboardingResponse, OnboardingState, UpdateOnboardingRequest } from '../types';

/**
 * T022: useOnboarding hook.
 * Fetches onboarding state via React Query and provides mutation for step updates.
 */

const ONBOARDING_QUERY_KEY = ['onboarding'] as const;

export function useOnboarding() {
  const queryClient = useQueryClient();

  const query = useQuery<OnboardingResponse>({
    queryKey: ONBOARDING_QUERY_KEY,
    queryFn: getOnboarding,
  });

  const mutation = useMutation<OnboardingState, Error, UpdateOnboardingRequest>({
    mutationFn: updateOnboardingStep,
    onSuccess: (updatedState) => {
      // Optimistically update the cached onboarding response
      queryClient.setQueryData<OnboardingResponse>(ONBOARDING_QUERY_KEY, (old) => {
        if (!old) return old;
        return { ...old, onboarding: updatedState };
      });
    },
  });

  return {
    /** Full onboarding response (store, onboarding state, scan progress, etc.) */
    data: query.data,
    /** The onboarding state subset */
    onboarding: query.data?.onboarding ?? null,
    /** Whether the initial fetch is loading */
    isLoading: query.isLoading,
    /** Whether the initial fetch errored */
    isError: query.isError,
    /** Error from the initial fetch */
    error: query.error,
    /** Mutation to update the current wizard step */
    updateStep: mutation.mutate,
    /** Async version of updateStep */
    updateStepAsync: mutation.mutateAsync,
    /** Whether a step update is in flight */
    isUpdating: mutation.isPending,
    /** Invalidate the onboarding cache (e.g., after scan trigger) */
    invalidate: () => queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY }),
  };
}
